/**
 * Event Team WebSocket — mirrors the incident/kanban hook structure
 * (jittered exponential backoff, on-open subscribe, 45s ping, seq-gap detection).
 *
 * Endpoint: `GET /api/notes/events/ws/item/{id}` — requires `event_read`;
 * upgrades (101) into the `event:{id}` realtime room (guide §5).
 */

import { useEffect, useRef, useCallback } from 'react';
import { EVENT_TEAM_API_BASE_URL } from '../services/eventTeamApi';
import type { EventWsEnvelope } from '../types/eventRealtime';

function httpBaseToWsBase(httpUrl: string): string {
  if (httpUrl.startsWith('https://')) return `wss://${httpUrl.slice('https://'.length)}`;
  if (httpUrl.startsWith('http://')) return `ws://${httpUrl.slice('http://'.length)}`;
  return httpUrl;
}

const WS_BASE = httpBaseToWsBase(EVENT_TEAM_API_BASE_URL.replace(/\/$/, ''));

function withJitter(ms: number): number {
  return ms + Math.floor(Math.random() * Math.min(2000, ms * 0.2));
}

function parseFrames(raw: string): EventWsEnvelope[] {
  const trimmed = raw.trim();
  if (!trimmed) return [];
  const lines = trimmed.includes('\n') ? trimmed.split('\n') : [trimmed];
  const out: EventWsEnvelope[] = [];
  for (const line of lines) {
    const s = line.trim();
    if (!s) continue;
    try {
      out.push(JSON.parse(s) as EventWsEnvelope);
    } catch {
      console.warn('Event WS: skipped non-JSON frame', s.slice(0, 120));
    }
  }
  return out;
}

export function eventWsUrl(eventId: string): string {
  return `${WS_BASE}/events/ws/item/${encodeURIComponent(eventId)}`;
}

export interface UseEventWebSocketOptions {
  eventId: string | undefined;
  /** When false, no socket (e.g. caller has no read permission yet). */
  enabled: boolean;
  onEnvelope: (env: EventWsEnvelope) => void;
  /** Fired when a `seq` gap is detected; caller should refetch canonical state. */
  onSeqGap?: () => void;
}

/**
 * Connects to `event:{id}` and dispatches every envelope through `onEnvelope`.
 *
 * `system.connected` / `system.subscribed` / `system.pong` / `system.error` frames
 * pass through as-is. Domain frames advance the local `seq` counter and fire
 * `onSeqGap` when a gap is detected (guide §5: treat WS as an invalidation
 * signal — payloads are convenience, not authority).
 */
export function useEventWebSocket(options: UseEventWebSocketOptions): void {
  const { eventId, enabled, onEnvelope, onSeqGap } = options;
  const onEnvelopeRef = useRef(onEnvelope);
  const onSeqGapRef = useRef(onSeqGap);
  useEffect(() => {
    onEnvelopeRef.current = onEnvelope;
  }, [onEnvelope]);
  useEffect(() => {
    onSeqGapRef.current = onSeqGap;
  }, [onSeqGap]);

  const lastSeqRef = useRef<number | null>(null);
  const reconnectAttemptRef = useRef(0);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimers = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (pingTimerRef.current) {
      clearInterval(pingTimerRef.current);
      pingTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!eventId || !enabled) {
      clearTimers();
      lastSeqRef.current = null;
      wsRef.current?.close();
      wsRef.current = null;
      return;
    }

    let cancelled = false;

    const scheduleReconnect = () => {
      if (cancelled) return;
      const attempt = reconnectAttemptRef.current++;
      const base = Math.min(60_000, 1000 * 2 ** Math.min(attempt, 16));
      const delay = withJitter(base);
      clearTimers();
      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null;
        connect();
      }, delay);
    };

    const connect = () => {
      if (cancelled) return;
      clearTimers();
      lastSeqRef.current = null;
      try {
        const url = eventWsUrl(eventId);
        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onopen = () => {
          if (cancelled) return;
          reconnectAttemptRef.current = 0;
          try {
            ws.send(
              JSON.stringify({
                action: 'subscribe',
                channels: [`event:${eventId}`],
              }),
            );
          } catch {
            /* optional */
          }
          pingTimerRef.current = setInterval(() => {
            try {
              if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ action: 'ping', nonce: String(Date.now()) }));
              }
            } catch {
              /* ignore */
            }
          }, 45_000);
        };

        ws.onmessage = (ev) => {
          if (cancelled) return;
          const text = typeof ev.data === 'string' ? ev.data : '';
          const envs = parseFrames(text);
          for (const env of envs) {
            if (env.type === 'system.connected' || env.type === 'system.subscribed') {
              lastSeqRef.current = typeof env.seq === 'number' ? env.seq : lastSeqRef.current;
              onEnvelopeRef.current(env);
              continue;
            }
            if (env.type === 'system.pong' || env.type === 'system.error') {
              onEnvelopeRef.current(env);
              continue;
            }

            const seq = env.seq;
            if (typeof seq === 'number') {
              const last = lastSeqRef.current;
              if (last !== null && seq !== last + 1) {
                onSeqGapRef.current?.();
                lastSeqRef.current = null;
              }
              lastSeqRef.current = seq;
            }
            onEnvelopeRef.current(env);
          }
        };

        ws.onerror = () => {
          ws.close();
        };

        ws.onclose = () => {
          wsRef.current = null;
          clearTimers();
          if (!cancelled) scheduleReconnect();
        };
      } catch {
        scheduleReconnect();
      }
    };

    connect();

    return () => {
      cancelled = true;
      clearTimers();
      reconnectAttemptRef.current = 0;
      lastSeqRef.current = null;
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [eventId, enabled, clearTimers]);
}
